import { Component, inject, signal } from '@angular/core';
import { NgClass } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';

import { SessionService } from '@services/session.service';
import { fadeInAnimation } from '@shared/animations/fade.animation';
import { FormComponent } from '../form/form.component';
import { ReportType } from '@admin/enums/ReportType.enum';

export class BreadcrumbItem {
  label: string = '';
  url: string = '';
}

@Component({
  selector: 'app-breadcrumb',
  imports: [RouterLink, NgClass],
  templateUrl: './breadcrumb.component.html',
  styleUrl: './breadcrumb.component.scss',
  animations: [fadeInAnimation],
})
export class BreadcrumbComponent extends FormComponent {
  session = inject(SessionService);
  route = inject(ActivatedRoute);

  breadcrumbs = signal<BreadcrumbItem[]>([]);
  title = signal('');
  type = signal('');

  campaignId = signal<number>(-1);
  typeId = signal<number>(-1);
  periodId = signal<number>(-1);
  segmentId = signal<number>(-1);
  clientId = signal<number>(-1);
  propertyId = signal<number>(-1);
  reportType = signal<ReportType | null>(null);

  ngOnInit(): void {
    this.session.campaign$.subscribe(() => {
      //  ! 1
      if (this.session.campaign) {
        this.campaignId.set(this.session.campaign.id);

        const params = this.route.snapshot.params;

        const title = `${
          this.session.campaign.clients.length > 0
            ? this.session.campaign.clients[0].name
            : ''
        } / ${this.session.campaign.name}`;

        this.title.set(title);
        this.breadcrumbs.set([]);
        this.propertyId.set(Number(params['propertyId'] ?? -1));
        this.clientId.set(Number(params['clientId'] ?? -1));

        const clientRoute =
          this.clientId() !== -1 ? '/client/' + this.clientId() : '';

        if (
          this.breadcrumbs().length === 0 ||
          this.breadcrumbs()[0].label !== this.session.campaign.name
        ) {
          this.breadcrumbs.update((breadcrumbs) => {
            breadcrumbs.unshift({
              label: this.session.campaign!.name,
              url: `/admin/campaigns/${
                this.session.campaign!.id
              }${clientRoute}`,
            });

            return breadcrumbs;
          });
        }

        // ! 2 -> call service
        this.periodId.set(Number(params['periodId'] ?? -1));

        if (this.periodId() > 0) {
          this.services.push(
            this.api.call(`admin/campaigns/${this.campaignId()}/types`)
          );
        }

        // ! 3 -> types
        this.type.set(decodeURIComponent(params['type'] || '').toUpperCase());
        this.typeId.set(Number(params['typeId'] ?? -1));

        if (this.typeId() > 0) {
          // admin/campaigns/5/types?type_id=25
          this.services.push(
            this.api.call(
              `admin/campaigns/${this.campaignId()}/types?type_id=${this.periodId()}`
            )
          );
        }

        // ! 4 -> segments
        this.segmentId.set(Number(params['segmentId'] ?? -1));

        if (this.segmentId() > 0) {
          this.services.push(
            this.api.call(`admin/segments/${this.segmentId()}`)
          );
        }

        if (this.services.length > 0) {
          this.loadServices();
        } else {
          this.handleReportType();
          this.stopLoading();
        }
      }
    });
  }

  override onLoadServicesSuccess(responses: any[]): void {
    const clientRoute =
      this.clientId() !== -1 ? '/client/' + this.clientId() : '';

    if (responses[0] && responses[0].status === 200) {
      const type = responses[0].data.rows.find(
        (item: any) => item.id === this.periodId()
      );

      this.breadcrumbs.update((breadcrumbs) => {
        if (this.periodId() > 0) {
          breadcrumbs.push({
            label: type?.property?.name || 'Quarter',
            url: `/admin/campaigns/${this.campaignId()}${clientRoute}/period/${this.periodId()}/property/${this.propertyId()}`,
          });
        }

        return breadcrumbs;
      });
    }

    if (responses[1] && responses[1].status === 200) {
      const type = responses[1].data.rows.find(
        (item: any) => item.id === this.typeId()
      );

      this.breadcrumbs.update((breadcrumbs) => {
        if (this.typeId() > 0) {
          breadcrumbs.push({
            label: type?.property?.name || 'Month',
            url: `/admin/campaigns/${this.campaignId()}/period/${this.periodId()}/property/${this.propertyId()}${clientRoute}/${
              this.type() ? this.type().toLowerCase() : 'type'
            }/${this.typeId()}`,
          });
        }

        return breadcrumbs;
      });
    }

    if (responses[2] && responses[2].status === 200) {
      this.breadcrumbs.update((breadcrumbs) => {
        if (this.segmentId() > 0) {
          breadcrumbs.push({
            label: responses[2].data.name || 'Month',
            url: '',
          });
        }

        return breadcrumbs;
      });
    }

    this.handleReportType();

    this.services = [];
    this.stopLoading();
  }

  handleReportType() {
    const data: any = this.route.snapshot.data;

    if (data.type) {
      this.reportType.set(data.type);

      this.breadcrumbs.update((breadcrumbs) => {
        if (this.reportType()) {
          breadcrumbs.push({
            label: this.reportType() || 'Report',
            url: '',
          });
        }

        return breadcrumbs;
      });
    }
  }

  override onLoadServicesError(response: any): void {
    this.services = [];
    this.stopLoading();
  }
}
